/* global Sys, Core, Resources */
Sys.ns("Core");

/**
 * The Local Storage Manager model.
 *
 * @class Core.LocalStorageManagerModel
 * @extends Core.Model
 * @uses Core.LocalStorageManager
 */
Core.LocalStorageManagerModel = {

    /**
     * @inheritdoc
     */
    constructor: function() {
        Core.LocalStorageManagerModel.superclass.constructor.apply(this, arguments);
    },

    /**
     * Write the data to local storage.
     *
     * @param {string} key The key of the data to store.
     * @param {string} value The value to store.
     * @returns {void}
     */
    writeToStorage: function(key, value) {
        if (this.readData("storageActive")) {
            if (typeof key === "string" && typeof value === "string") {
                this._setItemInStorage(this.accessKey + key, value);
            }
        }
    },

    /**
     * Read the data from local storage.
     *
     * @param {string} [key] The (optional) key of the data to locate. If it is not specified all data is returned.
     * @returns {string|Object|boolean} The string associated with the provided key, the object containing all data
     * in local storage or false if access to local storage is blocked.
     */
    readFromStorage: function(key) {
        var me = this;

        if (me.readData("storageActive")) {
            if (!Sys.isDefined(key)) {
                return me.getAllData();
            }

            if (typeof key === "string") {
                return me._getItemFromStorage(me.accessKey + key);
            }
        }

        return false;
    },

    /**
     * Remove the data from local storage.
     *
     * @param {string} key The key of the data to remove.
     * @returns {void}
     */
    removeFromStorage: function(key) {
        if (this.readData("storageActive")) {
            if (typeof key === "string") {
                this._removeItemFromStorage(this.accessKey + key);
            }
        }
    },

    /**
     * Check whether or not the key has data associated with it.
     *
     * @param {string} key The key of the data to locate.
     * @returns {boolean} Whether or not the key has data associated with it.
     */
    hasData: function(key) {
        if (Sys.isDefined(key)) {
            if (typeof this.readFromStorage(key) === "string") {
                return true;
            }
        }

        return false;
    },

    /**
     * @protected
     * @inheritdoc
     */
    setupData: function() {
        var queryString = Resources.readData("queryData");

        this.accessKey = "netent." + queryString.operatorId + "." + queryString.gameId + ".";

        this.storeData("storageActive", this.isLocalStorageSupported());
    },

    /**
     * Creates the access key from the operator-, game- and session id:s.
     *
     * @deprecated Not in use.
     * @param {string} operatorId Operator id.
     * @param {string} gameId Game id.
     * @param {string} sessionId Session id.
     * @returns {string|undefined} Access key.
     */
    createAccessKey: function(operatorId, gameId, sessionId) {
        if (Sys.isDefined(operatorId) && Sys.isDefined(gameId) && Sys.isDefined(sessionId)) {
            return this.hashValue(operatorId + "." + gameId + "." + sessionId) + ".";
        }
        return undefined;
    },

    /**
     * Activate access to local storage.
     *
     * @private
     * @returns {void}
     */
    activate: function() {
        this.storeData("storageActive", true);
    },

    /**
     * Deactivate access to local storage.
     *
     * @private
     * @returns {void}
     */
    deactivate: function() {
        this.storeData("storageActive", false);
    },

    /**
     * Get all data from local storage.
     *
     * @private
     * @returns {Object} The object containing all the key-value pairs from local storage.
     */
    getAllData: function() {
        var me = this,
            data = {},
            i = 0,
            key = me._getKeyFromStorage(i);

        while (key) {
            if (key.indexOf(me.accessKey) === 0) {
                data[key.replace(me.accessKey, "")] = me._getItemFromStorage(key);
            }
            ++i;
            key = me._getKeyFromStorage(i);
        }

        return data;
    },

    /**
     * Hashes the string value as a number.
     *
     * @private
     * @deprecated Not in use.
     * @param {string} value The value represented as a string.
     * @returns {number|null} The numerical representation of the string or null if the input was not a string.
     */
    hashValue: function(value) {
        var hash = 0x88394711,
            length,
            i;

        if (typeof value !== "string") {
            return null;
        }

        for (i = 0, length = value.length; i < length; i++) {
            hash += value.charCodeAt(i) * (i + 1);
        }

        return hash;
    },

    /**
     * Check whether or not local storage is supported.
     *
     * @private
     * @returns {boolean} Whether or not local storage is supported.
     */
    isLocalStorageSupported: function() {
        try {
            if ((typeof window.localStorage === "object")) {
                // If the quota is limited to zero setItem will throw a DOMException
                this._setItemInStorage("availabilityCheck", "true");
                this._removeItemFromStorage("availabilityCheck");
                return true;
            }
            return false;
        }
        catch (e) {
            return false;
        }
    },

    /**
     * Sets the key value pair in local storage.
     *
     * @private
     * @param {string} key The key.
     * @param {string} value The value.
     * @returns {void}
     */
    _setItemInStorage: function(key, value) {
        localStorage.setItem(key, value);
    },

    /**
     * Removes the key and its value from local storage.
     *
     * @private
     * @param {string} key The key.
     * @returns {void}
     */
    _removeItemFromStorage: function(key) {
        localStorage.removeItem(key);
    },

    /**
     * Gets the value associated with the key from local storage.
     *
     * @private
     * @param {string} key The key.
     * @returns {string} The value.
     */
    _getItemFromStorage: function(key) {
        return localStorage.getItem(key);
    },

    /**
     * Gets the key positioned on the index.
     *
     * @private
     * @param {number} index The index of the key.
     * @returns {string} The key.
     */
    _getKeyFromStorage: function(index) {
        return localStorage.key(index);
    }
};

Core.LocalStorageManagerModel = Sys.extend(Core.Model, Core.LocalStorageManagerModel, "Core.LocalStorageManagerModel");
